import React, { createContext, useContext, useState, useCallback } from 'react';
import { toast } from 'react-toastify';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedContent, setTranslatedContent] = useState({});

  const supportedLanguages = [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'hi', name: 'Hindi', nativeName: 'हिंदी' },
    { code: 'pa', name: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ' },
    { code: 'ur', name: 'Urdu', nativeName: 'اردو' }
  ];

  // Get backend URL from environment
  const getBackendUrl = useCallback(async () => {
    try {
      const envUrl = process.env.REACT_APP_BACKEND_URL;
      // If REACT_APP_BACKEND_URL is empty or undefined, use empty string 
      // so requests go through the same domain (Kubernetes ingress)
      if (!envUrl || envUrl.trim() === '') {
        return '';
      }
      return envUrl;
    } catch (error) {
      console.error('Error getting backend URL:', error);
      return '';
    }
  }, []);

  // New simplified page translation that preserves HTML structure
  const translatePage = useCallback(async (targetLanguage) => {
    if (targetLanguage === 'en') {
      // Reset to English - reload the page to get original content
      setCurrentLanguage('en');
      setTranslatedContent({});
      // Force a page refresh to restore original English text
      window.location.reload();
      return;
    }

    setIsTranslating(true);
    try {
      // Extract all text content that needs translation
      const textElements = document.querySelectorAll('[data-translatable]');
      const textsToTranslate = [];
      const elementMap = [];

      // If no elements are marked with data-translatable, find all text elements
      if (textElements.length === 0) {
        const allTextElements = document.querySelectorAll(
          'h1, h2, h3, h4, h5, h6, p, span:not(.no-translate), a:not(.no-translate), button:not(.no-translate):not(.language-selector-btn), label, li, td, th, div[class*="text-"]:not(.no-translate)'
        );
        
        allTextElements.forEach((element) => {
          // For buttons and links, we need to be more careful
          if (element.tagName === 'BUTTON' || element.tagName === 'A') {
            // Only translate if it's a simple text button/link (no complex children)
            const hasOnlyTextContent = Array.from(element.childNodes).every(node => 
              node.nodeType === Node.TEXT_NODE || 
              (node.nodeType === Node.ELEMENT_NODE && node.children.length === 0)
            );
            
            if (hasOnlyTextContent) {
              const text = element.textContent?.trim();
              if (text && text.length > 2 && !shouldSkipElement(element)) {
                textsToTranslate.push(text);
                elementMap.push(element);
              }
            }
          } else {
            // For other elements, use direct text content
            const directTextContent = getDirectTextContent(element);
            if (directTextContent && directTextContent.length > 2 && !shouldSkipElement(element)) {
              textsToTranslate.push(directTextContent);
              elementMap.push(element);
            }
          }
        });
      } else {
        textElements.forEach((element) => {
          const text = element.textContent?.trim();
          if (text && text.length > 2) {
            textsToTranslate.push(text);
            elementMap.push(element);
          }
        });
      }

      if (textsToTranslate.length === 0) {
        toast.warning('No text found to translate');
        return;
      }

      console.log(`Found ${textsToTranslate.length} text elements to translate`);

      // Translate all texts in bulk
      const BACKEND_URL = await getBackendUrl();
      const response = await fetch(`${BACKEND_URL}/api/translate/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          texts: textsToTranslate,
          target_language: targetLanguage,
          source_language: 'en'
        })
      });

      if (response.ok) {
        const result = await response.json();
        const translations = result.translations || [];

        console.log(`Received ${translations.length} translations`);

        // Apply translations directly to DOM elements while preserving HTML structure
        translations.forEach((translation, index) => {
          if (elementMap[index] && translation.translated_text) {
            // Store original HTML content as data attribute for restoration
            if (!elementMap[index].getAttribute('data-original-html')) {
              elementMap[index].setAttribute('data-original-html', elementMap[index].innerHTML);
            }
            
            // For buttons and links, replace textContent to preserve functionality
            if (elementMap[index].tagName === 'BUTTON' || elementMap[index].tagName === 'A') {
              elementMap[index].textContent = translation.translated_text;
            } else {
              // For other elements, try to preserve structure
              replaceTextInElement(elementMap[index], textsToTranslate[index], translation.translated_text);
            }
          }
        });

        // Also handle placeholders
        const placeholderElements = document.querySelectorAll('input[placeholder], textarea[placeholder]');
        const placeholderTexts = [];
        const placeholderElementMap = [];

        placeholderElements.forEach((element) => {
          const placeholder = element.getAttribute('placeholder');
          if (placeholder && placeholder.trim()) {
            placeholderTexts.push(placeholder);
            placeholderElementMap.push(element);
          }
        });

        if (placeholderTexts.length > 0) {
          const placeholderResponse = await fetch(`${BACKEND_URL}/api/translate/bulk`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              texts: placeholderTexts,
              target_language: targetLanguage,
              source_language: 'en'
            })
          });

          if (placeholderResponse.ok) {
            const placeholderResult = await placeholderResponse.json();
            const placeholderTranslations = placeholderResult.translations || [];

            placeholderTranslations.forEach((translation, index) => {
              if (placeholderElementMap[index] && translation.translated_text) {
                // Store original placeholder
                if (!placeholderElementMap[index].getAttribute('data-original-placeholder')) {
                  placeholderElementMap[index].setAttribute('data-original-placeholder', 
                    placeholderElementMap[index].getAttribute('placeholder'));
                }
                
                // Replace the placeholder
                placeholderElementMap[index].setAttribute('placeholder', translation.translated_text);
              }
            });
          }
        }

        setCurrentLanguage(targetLanguage);
        toast.success(`Page translated to ${supportedLanguages.find(l => l.code === targetLanguage)?.name}`);
      } else {
        throw new Error('Translation failed');
      }
    } catch (error) {
      console.error('Translation error:', error);
      toast.error(`Translation to ${supportedLanguages.find(l => l.code === targetLanguage)?.name} failed`);
    } finally {
      setIsTranslating(false);
    }
  }, [getBackendUrl, supportedLanguages]);

  // Helper function to get only direct text content (not from nested elements)
  const getDirectTextContent = (element) => {
    let text = '';
    for (let node of element.childNodes) {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent;
      }
    }
    return text.trim();
  };

  // Helper function to replace text while preserving HTML structure
  const replaceTextInElement = (element, originalText, translatedText) => {
    // If element has only text content, replace it directly
    if (element.childNodes.length === 1 && element.childNodes[0].nodeType === Node.TEXT_NODE) {
      element.textContent = translatedText;
      return;
    }

    // For elements with mixed content, replace only text nodes
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );

    const textNodes = [];
    let node = walker.nextNode();
    while (node) {
      textNodes.push(node);
      node = walker.nextNode();
    }

    // Replace text nodes that match the original text
    textNodes.forEach(textNode => {
      const nodeText = textNode.textContent.trim();
      if (nodeText && originalText.includes(nodeText)) {
        textNode.textContent = textNode.textContent.replace(nodeText, translatedText);
      }
    });
  };

  // Helper function to check if element should be skipped
  const shouldSkipElement = (element) => {
    // Skip if element is empty or contains only numbers/symbols
    const text = getDirectTextContent(element);
    if (!text || /^[\d\s\-\+\(\)\[\]\.,:;!?]*$/.test(text)) return true;
    
    // Skip if element is inside script, style, or other technical tags
    const skipTags = ['SCRIPT', 'STYLE', 'CODE', 'PRE', 'NOSCRIPT'];
    if (skipTags.includes(element.tagName)) return true;
    
    // Skip if element or its parent has specific classes that shouldn't be translated
    const skipClasses = ['no-translate', 'number', 'date', 'time', 'code', 'language-selector'];
    const hasSkipClass = skipClasses.some(cls => 
      element.classList.contains(cls) || 
      element.closest(`.${cls}`)
    );
    if (hasSkipClass) return true;
    
    // Skip if element is part of navigation or UI controls (unless specifically marked for translation)
    if (element.closest('.language-selector, .dropdown-menu, .btn-group')) return true;
    
    // Skip very short text
    if (text.length <= 2) return true;
    
    // Skip common English words that don't need translation
    const skipWords = ['©', '®', '™', 'PM', 'SHRI', 'API', 'URL', 'ID'];
    if (skipWords.includes(text.toUpperCase())) return true;
    
    return false;
  };

  // Get translated text for a specific key (legacy support)
  const getTranslatedText = useCallback((key, defaultText) => {
    if (currentLanguage === 'en') return defaultText;
    return translatedContent[key] || defaultText;
  }, [currentLanguage, translatedContent]);

  // Individual text translation (fallback method)
  const translateText = useCallback(async (text, targetLanguage) => {
    if (!text || (targetLanguage || currentLanguage) === 'en') return text;

    try {
      const BACKEND_URL = await getBackendUrl();
      const response = await fetch(`${BACKEND_URL}/api/translate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text,
          target_language: targetLanguage || currentLanguage,
          source_language: 'en'
        })
      });

      if (response.ok) {
        const result = await response.json();
        return result.translated_text;
      }
    } catch (error) {
      console.error('Text translation error:', error);
    }
    
    return text;
  }, [getBackendUrl, currentLanguage]);

  const value = {
    currentLanguage,
    setCurrentLanguage,
    supportedLanguages,
    isTranslating,
    translatePage,
    getTranslatedText,
    translateText,
    translatedContent
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};